"use client";

// ─── Odliczanie do końca promocji czasowej ───────────────────────
// Konwertujący komponent countdown. Renderuje się wyłącznie, gdy promocja
// jest aktywna (data w przyszłości). Po wygaśnięciu znika automatycznie.
// Warianty:
//   • "banner"  — pełna belka z gradientem i pulsującą ikoną (strona kursu),
//   • "compact" — kompaktowy pasek do koszyka / list,
//   • "inline"  — minimalny, jednoliniowy.

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Clock } from "lucide-react";

interface Remaining {
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function computeRemaining(target: number): Remaining {
  const total = Math.max(0, target - Date.now());
  const days = Math.floor(total / 86_400_000);
  const hours = Math.floor((total % 86_400_000) / 3_600_000);
  const minutes = Math.floor((total % 3_600_000) / 60_000);
  const seconds = Math.floor((total % 60_000) / 1000);
  return { total, days, hours, minutes, seconds };
}

export default function PromoCountdown({
  endsAt,
  variant = "banner",
  className = "",
}: {
  endsAt: string | Date | null | undefined;
  variant?: "banner" | "compact" | "inline";
  className?: string;
}) {
  const target = useMemo(() => {
    if (!endsAt) return null;
    const t = new Date(endsAt).getTime();
    return Number.isNaN(t) ? null : t;
  }, [endsAt]);

  // Start od `null` (identycznie na serwerze i kliencie). Wartosc zalezna od
  // czasu (Date.now()) liczymy dopiero po zamontowaniu w useEffect ponizej,
  // zeby uniknac niezgodnosci hydratacji (#418).
  const [remaining, setRemaining] = useState<Remaining | null>(null);

  useEffect(() => {
    // Brak aktywnej promocji — render i tak zwraca null (guard nizej), wiec
    // nie trzeba zerowac stanu.
    if (!target) return;
    // Celowy, jednorazowy odczyt po zamontowaniu: wartosc zalezy od Date.now(),
    // wiec liczymy ja dopiero na kliencie, aby uniknac niezgodnosci hydratacji
    // (#418). Kolejne aktualizacje ida juz z setInterval (callback asynchroniczny).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRemaining(computeRemaining(target));
    const id = setInterval(() => {
      const next = computeRemaining(target);
      setRemaining(next);
      if (next.total <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [target]);

  // Promocja nieaktywna lub zakończona → nic nie renderujemy.
  if (!target || !remaining || remaining.total <= 0) return null;

  const pad = (n: number) => String(n).padStart(2, "0");
  const showDays = remaining.days > 0;

  // ── Wariant inline ──
  if (variant === "inline") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400 ${className}`}
        aria-label="Promocja kończy się wkrótce"
      >
        <Clock size={13} className="shrink-0" />
        <span className="tabular-nums">
          {showDays && `${remaining.days}d `}
          {pad(remaining.hours)}:{pad(remaining.minutes)}:{pad(remaining.seconds)}
        </span>
      </span>
    );
  }

  // ── Wariant compact (koszyk) ──
  if (variant === "compact") {
    return (
      <div
        className={`flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 dark:border-rose-500/30 dark:bg-rose-500/10 ${className}`}
        role="timer"
        aria-live="off"
      >
        <Flame size={14} className="shrink-0 text-rose-500" />
        <span className="text-[11px] font-semibold text-rose-700 dark:text-rose-300">
          Promocja kończy się za
        </span>
        <span className="ml-auto font-mono text-xs font-bold tabular-nums text-rose-700 dark:text-rose-300">
          {showDays && `${remaining.days}d `}
          {pad(remaining.hours)}:{pad(remaining.minutes)}:{pad(remaining.seconds)}
        </span>
      </div>
    );
  }

  // ── Wariant banner (strona kursu) ──
  const units: { label: string; value: number; show: boolean }[] = [
    { label: "dni", value: remaining.days, show: showDays },
    { label: "godz", value: remaining.hours, show: true },
    { label: "min", value: remaining.minutes, show: true },
    { label: "sek", value: remaining.seconds, show: true },
  ];

  return (
    <div
      className={`overflow-hidden rounded-xl bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 p-[1.5px] shadow-lg shadow-rose-500/20 ${className}`}
      role="timer"
      aria-live="off"
    >
      <div className="rounded-[10px] bg-white px-3.5 py-2.5 dark:bg-slate-900">
        <div className="flex items-center gap-2">
          <motion.span
            animate={{ scale: [1, 1.18, 1] }}
            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-500"
          >
            <Flame size={14} />
          </motion.span>
          <p className="text-xs font-bold text-slate-700 dark:text-slate-200">
            Promocja kończy się za:
          </p>
          <div className="ml-auto flex items-center gap-1">
            {units
              .filter((u) => u.show)
              .map((u, i, arr) => (
                <div key={u.label} className="flex items-center gap-1">
                  <div className="flex min-w-[34px] flex-col items-center rounded-md bg-slate-900 px-1.5 py-1 dark:bg-slate-800">
                    <span className="font-mono text-sm font-extrabold leading-none tabular-nums text-white">
                      {pad(u.value)}
                    </span>
                    <span className="mt-0.5 text-[8px] font-medium uppercase tracking-wide text-slate-400">
                      {u.label}
                    </span>
                  </div>
                  {i < arr.length - 1 && (
                    <span className="text-sm font-bold text-slate-400">:</span>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
